<template>
    <div :class="`convention-sections-table ${mode}`">
        <div class="row" v-for="sectionName in sectionNames" :key="sectionName">
            <div class="col" v-for="cspCode in cspCodes" :key="cspCode">
                <convention-sections-table-cell
                    :sectionName="sectionName"
                    :cspCode="cspCode"
                    :isParentEnveloppe="isParentEnveloppe"
                    :sectionsDraft="convention.sectionsDraft"
                    :cspConfig="convention.cspConfig"
                    :activeCspCodesBySectionName="activeCspCodesBySectionName"
                    :commonGroups="commonGroups"
                    @goto="goToSection"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { chain, find, isArray, keyBy, keys } from "lodash";
import { EventBus } from "src/bus/eventBus";
import conventionManager from "src/components/convention/managers/conventionManager";
import ConventionSectionsTableCell from "./ConventionSectionsTableCell.vue";

export default Vue.extend({
    components: { ConventionSectionsTableCell },
    props: ["convention", "container"],
    data() {
        return {
            mode: "table",
            commonGroups: null,
            activeCspCodesBySectionName: null,
        };
    },
    mounted() {
        EventBus.$on("convention-sections:scrolled", mode => (this.mode = mode));
        EventBus.$on("convention-sections:updated", () => {
            this.commonGroups = this.buildCommonGroups();
            this.activeCspCodesBySectionName = this.buildActiveCspCodesBySectionName();
        });
    },
    computed: {
        cspCodes() {
            return keys(this.$privateDico.convention.csps);
        },
        sectionNames() {
            return keys(this.$privateDico.convention.themes);
        },
        isParentEnveloppe() {
            return conventionManager.isParentEnveloppe(this.convention);
        },
        sectionsByName() {
            return this.convention && this.convention.sections
                ? keyBy(this.convention.sections, section => section.key)
                : [];
        },
        isAllCsp() {
            return (
                this.convention &&
                this.convention.cspConfig &&
                this.convention.cspConfig.allIncluded
            );
        },
    },
    methods: {
        buildActiveCspCodesBySectionName() {
            return chain(this.sectionNames)
                .map(sectionName => {
                    const section = this.sectionsByName[sectionName];
                    if (!section) {
                        return [sectionName, []];
                    }

                    const el = this.getSectionElement(section);
                    const codes = chain(this.cspCodes)
                        .map(cspCode =>
                            this.isAllCsp || el.querySelector(`.${cspCode}`) ? cspCode : null
                        )
                        .compact()
                        .value();
                    return [sectionName, codes];
                })
                .fromPairs()
                .value();
        },
        buildCommonGroups() {
            if (!isArray(this.sectionNames)) return;
            const commonGroups = {};
            this.sectionNames.forEach(sectionName => {
                if (!isArray(this.cspCodes)) return;
                const section = this.sectionsByName[sectionName];

                this.cspCodes.forEach((cspCode, index) => {
                    const nextCspCode = this.cspCodes[index + 1];
                    if (!section) {
                        return;
                    }

                    if (!nextCspCode) {
                        return;
                    }

                    const el = this.getSectionElement(section);
                    const cspTotal = el.querySelectorAll(`.${cspCode}`).length;
                    const nextCspTotal = el.querySelectorAll(`.${nextCspCode}`).length;
                    const commonTotal = el.querySelectorAll(`.${cspCode}.${nextCspCode}`).length;
                    const isCommon = cspTotal == nextCspTotal && nextCspTotal == commonTotal;

                    if (isCommon) {
                        if (!commonGroups[sectionName]) commonGroups[sectionName] = [];
                        const group = find(commonGroups[sectionName], g => g.indexOf(cspCode) > -1);
                        if (group) {
                            group.push(nextCspCode);
                        } else {
                            commonGroups[sectionName].push([cspCode, nextCspCode]);
                        }
                    }
                });
            });
            return commonGroups;
        },
        getSectionElement(section) {
            return this.container.querySelector(`#section-${section.key}`);
        },
        goToSection({ sectionName, cspCode }) {
            const section = this.sectionsByName[sectionName];
            if (!section) {
                return;
            }
            const commonGroup = this.commonGroups[sectionName]
                ? find(this.commonGroups[sectionName], g => g.indexOf(cspCode) > -1)
                : null;
            const filters = commonGroup ? commonGroup : [cspCode];
            this.$emit("select", { filters, section });
        },
    },
});
</script>

<style scoped lang="sass">
.convention-sections-table
    position: sticky
    top: 200px
    transition: opacity .3s ease
    +r(1400)
        top: 180px
.row
    display: flex

.col
    padding-top: 4px
    padding-bottom: 4px
    width: 100px
    position: relative
    height: 29px
    box-sizing: border-box
    +r(1400)
        height: 24px

.convention-sections-table.content
    opacity: 0

@media print
    .convention-sections-table
        position: static
        margin-left: 30%
        margin-bottom: 10mm

        .row
            margin: 0.75mm 0

        .col
            margin: 0
            padding: 0.5mm 0.75mm
            width: 20%

        &.content
            opacity: 1
</style>
